import { centsToInteger, safeFormatMessage } from '@10xlabs/utils'
import { Formik, getIn, useFormikContext } from 'formik'
import { valuesIn } from 'lodash'
import { ReactElement } from 'react'
import { useIntl, FormattedMessage, defineMessages, MessageDescriptor } from 'react-intl'
import { NavLink } from 'react-router-dom'

import Card from '@10xLabs/react-web/components/Card'
import { Column } from '@10xLabs/react-web/components/Column'
import { Row } from '@10xLabs/react-web/components/Row'
import { InputCurrency } from 'components/InputCurrency'
import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Input } from 'components/Input'
import { Label } from 'components/v2/Label'
import { Select } from 'components/v2/Select'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { ISalesChannel } from 'features/Services/components/NewServicesV2/components/OfferSettingsCard/types'
import { SelectSalesChannelsV2 } from 'features/Services/components/SelectSalesChannelsV2'
import { GTAncillaryTypeInput, GTLanguage } from 'gql/types.generated'
import { getUnprocessableErrors } from 'utils/error'
import { getLabelTranslation } from 'utils/intl'
import { IAncillaryFormProps, INewAncillaryForm, INewAncillaryProps } from './types'
import { schema } from './schema'
import { ancillaryTypeMessages } from 'features/Ancillaries/components/NewAncillaryForm/messages'
import { canSelectCompany, getDefaultCompanyForForm } from 'features/Login/utils'
import { useLogin } from 'features/Login/hooks'
import { CompanySuggestion } from 'gql/generated/graphql'

const messages: Record<string, MessageDescriptor> = defineMessages({
  namePlaceholder: {
    id: 'ancillaries.form.name.placeholder',
    defaultMessage: 'Enter a name'
  },
  salesChannelsPlaceholder: {
    id: 'ancillaries.form.salesChannels.placeholder',
    defaultMessage: 'Select a sales channel'
  },
  companyLabel: {
    id: 'ancillaries.form.company.label',
    defaultMessage: 'Company'
  },
  companyPlaceholder: {
    id: 'ancillaries.form.company.placeholder',
    defaultMessage: 'Select company'
  },
  companyLineLabel: {
    id: 'ancillaries.form.companyLine.label',
    defaultMessage: 'Company Line'
  },
  enLabelPlaceholder: {
    id: 'ancillaries.form.enLabel.placeholder',
    defaultMessage: 'Enter a label in English'
  },
  esLabelPlaceholder: {
    id: 'ancillaries.form.esLabel.placeholder',
    defaultMessage: 'Enter a label in Spanish'
  },
  selectType: {
    id: 'ancillaries.form.status.placeholder',
    defaultMessage: 'Select type'
  },
  active: {
    id: 'ancillaries.form.active.label',
    defaultMessage: 'Active'
  },
  inactive: {
    id: 'ancillaries.form.inactive.label',
    defaultMessage: 'Inactive'
  }
})

const AncillaryForm = ({ error, loading, isEdit }: IAncillaryFormProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  const { values, touched, errors, handleBlur, setFieldValue, handleSubmit, handleChange } =
    useFormikContext<INewAncillaryForm>()

  const handleSalesChannelsChange = (salesChannels: ISalesChannel[]): void => {
    setFieldValue('salesChannels', salesChannels)
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <Card.Content noHeader>
        <Row>
          <Column width="50%">
            {canSelectCompany(login.accessToken) && (
              <CompanyAutocomplete
                id="company"
                label={intl.formatMessage(messages.companyLabel)}
                placeholder={intl.formatMessage(messages.companyPlaceholder)}
                value={values.company.name}
                onChange={(value: CompanySuggestion): void => {
                  setFieldValue('company', {
                    id: value.id,
                    name: value.name
                  })
                }}
                touched={touched.company && touched.company.id}
                errors={errors.company && errors.company.id}
                disabled={isEdit}
              />
            )}
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="ancillaries.new.form.name" defaultMessage="Name" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Select
              id="type"
              value={values.type}
              onChange={(e): void => {
                setFieldValue('type', e.target.value)
              }}
              touched={Boolean(touched.type)}
              errors={errors.type as string}
              label={<FormattedMessage id="dictionary.type" defaultMessage="Type" />}
            >
              <option value="">{intl.formatMessage(messages.selectType)}</option>
              {valuesIn(GTAncillaryTypeInput).map((type): TSFixMe => {
                return (
                  <option value={type} key={type}>
                    {safeFormatMessage({
                      intl,
                      messages: ancillaryTypeMessages,
                      key: type
                    })}
                  </option>
                )
              })}
            </Select>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="esLabel"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.esLabel}
              placeholder={intl.formatMessage(messages.esLabelPlaceholder)}
              touched={touched.esLabel}
              errors={errors.esLabel}
              label={<FormattedMessage id="ancillaries.form.esLabel.label" defaultMessage="Label Es" />}
            />
          </Column>
          <Column width="50%">
            <Input
              id="enLabel"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.enLabel}
              placeholder={intl.formatMessage(messages.enLabelPlaceholder)}
              touched={touched.enLabel}
              errors={errors.enLabel}
              label={<FormattedMessage id="ancillaries.form.enLabel.label" defaultMessage="Label En" />}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Label>
              <FormattedMessage id="ancillaries.form.salesChannels.label" defaultMessage="Sales Channels" />
            </Label>
            <SelectSalesChannelsV2
              id="salesChannels"
              values={values.salesChannels}
              placeholder={intl.formatMessage(messages.salesChannelsPlaceholder)}
              onChange={handleSalesChannelsChange}
              touched={getIn(touched, 'salesChannels')}
              errors={getIn(errors, 'salesChannels')}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="ancillaries.form.price.label" defaultMessage="Price" />
            </Label>
            <InputCurrency
              id="price"
              suffix=""
              value={values.price ? Number(values.price) : ''}
              placeholder="$0.00"
              onChange={(value: string): void => {
                setFieldValue('price', value)
              }}
              onBlur={handleBlur}
              touched={Boolean(touched.price)}
              errors={errors.price as string}
              hideSuffix
            />
          </Column>
        </Row>
      </Card.Content>
      <Card.Footer>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              {isEdit ? (
                <FormattedMessage
                  id="ancillaries.form.errorUpdate"
                  defaultMessage="There was an error updating the ancillary"
                />
              ) : (
                <FormattedMessage
                  id="ancillaries.form.errorCreate"
                  defaultMessage="There was an error creating the ancillary"
                />
              )}
            </ErrorMessage>
          </div>
        )}
        <Card.FooterActions>
          <Button as={NavLink} to="/ancillaries/luggage" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </Card.FooterActions>
      </Card.Footer>
    </form>
  )
}

export const NewAncillary = ({ ancillary, onSubmit, error, loading, isEditView }: INewAncillaryProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  const initialValues: INewAncillaryForm = {
    name: ancillary?.name ?? '',
    enLabel: ancillary?.label ? getLabelTranslation(ancillary?.label, GTLanguage.En) : '',
    esLabel: ancillary?.label ? getLabelTranslation(ancillary?.label, GTLanguage.Es) : '',
    type: ancillary?.type ?? '',
    price: ancillary?.price ? String(centsToInteger(ancillary.price)) : '',
    salesChannels: ancillary?.conditions?.salesChannels ?? [],
    company: ancillary?.company || getDefaultCompanyForForm(login.accessToken)
  }

  return (
    <Formik initialValues={initialValues} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {(): ReactElement => {
        return <AncillaryForm error={error} loading={loading} isEdit={isEditView} />
      }}
    </Formik>
  )
}
